import React, { useState, useEffect, ReactNode } from 'react'
import { useRouter } from 'next/router'
import { isBrowser, checkStoreIs, getAccountPageUrl } from '@helpers/env'
import Translations from '@helpers/translations'
import { objectKeys } from '@helpers/utils'
import { ReturnsHeaderImage } from '@components/returns/ReturnsHeaderImage'
import { FormContent } from '@components/Form/FormContent'
import Notification from '@components/ui/Notification'
import { SubmitButton } from '@components/ui/Button'
import styles from './styles.module.scss'
import Link from '@components/ui/Link'
import { ItemSelectionOnSubmitProps } from '../ItemsSelection'

type HeaderType = {
  title: string
  byline: string[]
  image: {
    mobile: string
    desktop: string
  }
}

type ContentType = {
  id: string
  question: string
  type: string
  required: boolean
  answers: string[]
  inputMode?: string
  pattern?: string
  hidden: boolean
}

export type FormData = {
  errorNotification: ReactNode
  header: HeaderType
  buttonCopy: string
  content: ContentType[]
}

type Values = Record<string, string>

type OrderFormProps = {
  formData: FormData
  onSubmit: (args: ItemSelectionOnSubmitProps) => void
}

function transformOrderNumber(orderNumber: string) {
  const isNonGBStore = checkStoreIs('eu') || checkStoreIs('us')

  if (
    orderNumber &&
    isNonGBStore &&
    !orderNumber.toUpperCase().includes(process.env.SHOP_LOCALE)
  ) {
    return `${process.env.SHOP_LOCALE}${orderNumber}`
  }

  return orderNumber.toUpperCase()
}

function OrderForm({ formData, onSubmit }: OrderFormProps) {
  const router = useRouter()
  const { error } = router.query

  const [values, setValues] = useState<Values>({})
  const [buttonState, setButtonState] = useState<
    'loading' | 'ready' | 'disabled'
  >('disabled')

  useEffect(() => {
    const isComplete = objectKeys(values).length === formData.content.length
    setButtonState(isComplete ? 'ready' : 'disabled')
  }, [values, formData.content])

  const handleChange = (answer: Values) => {
    setValues({
      ...values,
      ...objectKeys(answer).reduce(
        (json, key) =>
          key === 'order_number'
            ? { ...json, [key]: answer[key].replace(/[^A-Za-z0-9]/g, '') } // remove non alphanumeric characters from order_number if typed by user
            : { ...json, [key]: answer[key] },
        {}
      )
    })
  }

  const handleOnSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault()
    setButtonState('loading')

    const normaliseOrderNumber = {
      ...values,
      order_number: transformOrderNumber(values.order_number)
    }

    onSubmit({
      queryParams: {
        ...normaliseOrderNumber
      }
    } as ItemSelectionOnSubmitProps)
  }

  return (
    isBrowser() && (
      <>
        <ReturnsHeaderImage content={formData.header} />
        {error && (
          <Notification type="error" content={formData.errorNotification} />
        )}
        <form className={styles.form} onSubmit={handleOnSubmit} noValidate>
          {formData.content.map(data => (
            <FormContent
              changeValue={handleChange}
              values={values}
              key={data.id}
              data={data}
            />
          ))}
          <div className={styles.form__button}>
            <SubmitButton
              type="submit"
              fullWidth="always"
              state={buttonState}
              disabled={buttonState === 'loading' || buttonState === 'disabled'}
            >
              {formData.buttonCopy}
            </SubmitButton>
          </div>
          <p className={styles.form__preOrderLink}>
            {Translations.localise('common.form.preorder')}
            <Link href={getAccountPageUrl('pages/my-pre-orders')}>
              {Translations.localise('common.ctas.click-here')}
            </Link>
          </p>
        </form>
      </>
    )
  )
}

export default OrderForm
